import { useEffect } from "react";
import { useDispatch } from "react-redux";
import create from "zustand";

import { createWebSocket } from "./utils";

export type Job = {
	_id: string;
	finished: boolean;
	has_progress: boolean | null;
	name: string;
	progress_count: number;
	progress_total: number;
	redirectURL: string;
	seen: boolean | null;
	started: boolean;
	task_id: string | null;
	title: string;
	todos: any | null; // Could be more specific if we know the todos structure
	type: string | null;
};

type WebSocketMessage = {
	data: any;
	type?: string;
};

type ReduxDispatch = (action: any) => void;

type WebSocketJobsState = {
	socket: WebSocket | null;
	jobsListCanClose: boolean;
	isInitialized: boolean;
	reduxDispatch: ReduxDispatch | null;
	jobs: Job[];
	isConnected: boolean;
	isConnecting: boolean;
	isDisconnecting: boolean;

	connect: (url: string) => void;
	disconnect: () => void;
	sendMessage: (msg: string) => void;
	allowJobsListToBeClosed: () => void;
	disallowJobsListToBeClosed: () => void;
	onMessage: (message: string) => void;
	setInitialized: (initialized: boolean) => void;
	setReduxDispatch: (dispatch: ReduxDispatch | null) => void;
	dispatchToReduxJobs: (action: "WEBSOCKET:ERROR" | "UPDATE", jobs: Job[], noPrint: boolean) => void;
};

export const useWebSocketJobsStore = create<WebSocketJobsState>((set, get) => ({
	socket: null,
	isConnected: false,
	isConnecting: false,
	isDisconnecting: false,
	jobsListCanClose: true,
	isInitialized: false,
	reduxDispatch: null,
	jobs: [],

	allowJobsListToBeClosed: () => {
		set({ jobsListCanClose: true });
	},
	disallowJobsListToBeClosed: () => {
		set({ jobsListCanClose: false });
	},
	setInitialized: (initialized) => {
		set({ isInitialized: initialized });
	},
	setReduxDispatch: (dispatch) => {
		set({ reduxDispatch: dispatch });
	},
	dispatchToReduxJobs: (action, jobs, noPrint) => {
		const state = get();
		if (state.reduxDispatch) {
			state.reduxDispatch({ type: `JOBS:${action}`, jobs, noPrint });
		} else {
			console.warn("Redux dispatch not available. Call setReduxDispatch first.");
		}
	},
	onMessage: (messageData) => {
		const state = get();

		try {
			// Parse the message if it's a string
			const message: WebSocketMessage = typeof messageData === "string" ? JSON.parse(messageData) : messageData;

			// Run initialization logic on first message
			if (!state.isInitialized) {
				// Handle WebSocket error by dispatching to Redux
				if (message.type === "error") {
					state.dispatchToReduxJobs("WEBSOCKET:ERROR", [], true);
				}

				set({ isInitialized: true });
				set({ jobs: message as unknown as Job[] });
			}

			// Handle different message types and forward to Redux
			if (message) {
				const jobs = message as unknown as Job[];

				state.dispatchToReduxJobs("UPDATE", jobs, true);
			}
		} catch (error) {
			// Dispatch error to Redux
			state.dispatchToReduxJobs("WEBSOCKET:ERROR", [], true);
		}
	},
	connect: (url) => {
		const ws = createWebSocket(url);

		ws.onopen = () => {
			set({ isConnected: true, isConnecting: false });
		};
		ws.onmessage = (event) => {
			get().onMessage(event.data);
		};
		ws.onclose = () => {
			set({
				socket: null,
				isConnected: false,
				isDisconnecting: false,
				isInitialized: false,
			});
		};
		ws.onerror = (error) => {
			console.error("WebSocket error:", error);
			set({ isConnecting: false, isDisconnecting: false });
		};

		set({ socket: ws, isConnecting: true });
	},
	disconnect: () => {
		const socket = get().socket;
		if (socket) {
			set({ isDisconnecting: true });
			socket.close();
		}
	},
	sendMessage: (msg) => {
		get().socket?.send(msg);
	},
}));

/**
 * Hook to automatically connect the Zustand WebSocket store to Redux
 * Call this in a component that has access to Redux dispatch
 *
 * Example usage:
 * ```tsx
 * function MyComponent() {
 *   useWebSocketReduxBridge(); // Set up the bridge
 *
 *   const { connect, disconnect, isConnected } = useWebSocketJobsStore();
 *
 *   useEffect(() => {
 *     connect('/api/user_jobs/status/');
 *     return () => disconnect();
 *   }, []);
 *
 *   return <div>WebSocket Status: {isConnected ? 'Connected' : 'Disconnected'}</div>;
 * }
 * ```
 */
export const useWebSocketReduxBridge = () => {
	const dispatch = useDispatch();
	const setReduxDispatch = useWebSocketJobsStore((state) => state.setReduxDispatch);

	useEffect(() => {
		// Set up the bridge between Zustand and Redux
		setReduxDispatch(dispatch);

		return () => {
			// Clean up on unmount
			setReduxDispatch(null);
		};
	}, [dispatch, setReduxDispatch]);
};
